import { useRef, useMemo, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, MeshDistortMaterial, Stars } from '@react-three/drei';
import * as THREE from 'three';

function ParticleField() {
  const ref = useRef();
  const count = 120;

  const [positions, colors] = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 20;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 8;
      const t = Math.random();
      // blue to purple gradient
      col[i * 3] = 0.15 + t * 0.4;
      col[i * 3 + 1] = 0.3 + t * 0.1;
      col[i * 3 + 2] = 0.9;
    }
    return [pos, col];
  }, []);

  useFrame(({ clock }) => {
    ref.current.rotation.y = clock.elapsedTime * 0.04;
    ref.current.rotation.x = Math.sin(clock.elapsedTime * 0.02) * 0.1;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" array={positions} count={count} itemSize={3} />
        <bufferAttribute attach="attributes-color" array={colors} count={count} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.06} vertexColors transparent opacity={0.7} sizeAttenuation />
    </points>
  );
}

function FloatingOrb({ position, color, speed = 1, distort = 0.4, radius = 1 }) {
  const mesh = useRef();
  useFrame(({ clock }) => {
    mesh.current.rotation.x = clock.elapsedTime * 0.3 * speed;
    mesh.current.rotation.y = clock.elapsedTime * 0.2 * speed;
  });

  return (
    <Float speed={speed * 1.5} rotationIntensity={0.5} floatIntensity={1.2}>
      <mesh ref={mesh} position={position}>
        <sphereGeometry args={[radius, 64, 64]} />
        <MeshDistortMaterial
          color={color}
          distort={distort}
          speed={2}
          transparent
          opacity={0.15}
          roughness={0}
          metalness={0.1}
        />
      </mesh>
    </Float>
  );
}

function SpinningLogo() {
  const group = useRef();
  const inner = useRef();

  useFrame(({ clock }) => {
    group.current.rotation.y = clock.elapsedTime * 0.5;
    group.current.rotation.x = Math.sin(clock.elapsedTime * 0.3) * 0.2;
    inner.current.rotation.y = -clock.elapsedTime * 0.8;
    inner.current.rotation.z = clock.elapsedTime * 0.4;
  });

  return (
    <Float speed={2} floatIntensity={1.5} rotationIntensity={0.3}>
      <group ref={group}>
        {/* Outer icosahedron wireframe */}
        <mesh>
          <icosahedronGeometry args={[1.6, 1]} />
          <meshStandardMaterial
            color="#6366f1"
            wireframe
            transparent
            opacity={0.4}
          />
        </mesh>
        {/* Inner solid octahedron */}
        <mesh ref={inner}>
          <octahedronGeometry args={[0.9]} />
          <meshStandardMaterial
            color="#818cf8"
            metalness={0.8}
            roughness={0.1}
            emissive="#4f46e5"
            emissiveIntensity={0.3}
          />
        </mesh>
        {/* Core glow sphere */}
        <mesh>
          <sphereGeometry args={[0.45, 32, 32]} />
          <meshStandardMaterial
            color="#c7d2fe"
            emissive="#818cf8"
            emissiveIntensity={1.2}
            transparent
            opacity={0.9}
          />
        </mesh>
      </group>
    </Float>
  );
}

function WireGrid() {
  const ref = useRef();
  useFrame(({ clock }) => {
    ref.current.rotation.x = clock.elapsedTime * 0.04;
    ref.current.rotation.z = clock.elapsedTime * 0.02;
  });
  return (
    <mesh ref={ref} position={[0, 0, -3]} rotation={[-Math.PI / 2.5, 0, 0]}>
      <torusGeometry args={[4, 0.01, 2, 80]} />
      <meshStandardMaterial color="#6366f1" transparent opacity={0.1} />
    </mesh>
  );
}

export function HeroCanvas() {
  return (
    <Canvas
      camera={{ position: [0, 0, 6], fov: 55 }}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      dpr={[1, 2]}
    >
      <ambientLight intensity={0.5} />
      <pointLight position={[5, 5, 5]} intensity={1.5} color="#818cf8" />
      <pointLight position={[-5, -3, 2]} intensity={0.8} color="#60a5fa" />
      <Suspense fallback={null}>
        <Stars radius={30} depth={20} count={300} factor={2} fade speed={0.5} />
        <ParticleField />
        <FloatingOrb position={[-3.5, 1.5, -1]} color="#6366f1" speed={0.8} radius={2} distort={0.5} />
        <FloatingOrb position={[3, -1, -2]} color="#3b82f6" speed={1.2} radius={1.5} distort={0.3} />
        <WireGrid />
      </Suspense>
    </Canvas>
  );
}

export function LogoCanvas() {
  return (
    <Canvas
      camera={{ position: [0, 0, 5], fov: 50 }}
      style={{ width: '100%', height: '100%' }}
      dpr={[1, 2]}
    >
      <ambientLight intensity={0.6} />
      <pointLight position={[3, 3, 3]} intensity={2} color="#818cf8" />
      <pointLight position={[-3, -2, 1]} intensity={1} color="#60a5fa" />
      <Suspense fallback={null}>
        <SpinningLogo />
      </Suspense>
    </Canvas>
  );
}
